import { showDialogInfo } from "@/hooks/use-dialog-info"
import type { UndoRedoResponse } from "@/types/action"
import type { ItineraryClearOnly, ItineraryEvent } from "@/types/itinerary"
import type { TimeFrame, TripRef } from "@/types/trip"

import {
  itineraryEventToAmplitudeProperties,
  redoResponseToAmplitudeProperties,
  undoResponseToAmplitudeProperties,
} from "@/lib/analytics/amplitude-properties"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import {
  isItineraryMoveOnlyUpdate,
  prepareAddCustomItineraryEventAction,
  prepareAddPlaceItineraryEventAction,
  prepareUpdateItineraryEventAction,
  type PreparedAddItineraryEventAction,
  type PreparedUpdateItineraryEventAction,
} from "@/lib/itinerary-action-builders"
import {
  clearItinerarySection,
  createPreparedItineraryEvent,
  removeItineraryEventById,
  runRedoItinerary,
  runUndoItinerary,
  updatePreparedItineraryEvent,
} from "@/lib/itinerary-action-executors"
import {
  getAddItineraryActionToastContext,
  getItineraryEventToastContext,
  getUpdateItineraryActionToastContext,
  showItineraryActionToast,
  type ItineraryActionToastAction,
  type ItineraryActionToastClearAction,
  type ItineraryActionToastOptions,
} from "@/lib/itinerary-action-toasts"
import {
  UIWhereChat,
  type AddCustomItineraryEventActionInput,
  type AddPlaceItineraryEventActionInput,
  type ClearItineraryActionResponse,
  type RemoveItineraryEventActionResponse,
  type UIWhere,
  type UndoRedoResponseAndMessage,
  type UpdateItineraryEventActionInput,
} from "@/lib/itinerary-action-types"
import {
  describeUndoRedoResponse,
  type UndoRedoDirection,
} from "@/lib/itinerary-undo-redo-presenter"
import { isBooked } from "@/lib/itinerary-utils"
import type { AppDispatch } from "@/lib/storage/redux-store"
import { toastManager } from "@/lib/toast"

export function createItineraryActions({
  dispatch,
  tripRef,
  tripTimeFrame,
  uiWhere = UIWhereChat,
  silent = false,
  tripSheetOpen = false,
}: CreateItineraryActionsOptions): ItineraryActions {
  let nextState: ItineraryToastNextState = "init"

  function requireTripRef(): TripRef {
    if (!tripRef) {
      throw new Error(
        "createItineraryActions: tripRef is required for this operation"
      )
    }
    return tripRef
  }

  async function addPlaceEvent(addProps: AddPlaceItineraryEventActionInput) {
    const addAction = prepareAddPlaceItineraryEventAction(addProps)
    return await handleAddEvent(addAction)
  }

  async function addCustomEvent(
    addCustomProps: AddCustomItineraryEventActionInput
  ) {
    const addAction = prepareAddCustomItineraryEventAction(addCustomProps)
    return await handleAddEvent(addAction)
  }

  async function changeEvent(changeProps: UpdateItineraryEventActionInput) {
    const updateAction = prepareUpdateItineraryEventAction(changeProps)
    return await handleChangeEvent(updateAction)
  }

  async function removeEvent(originalEvent: ItineraryEvent) {
    if (isBooked(originalEvent)) {
      showDialogInfo({
        title: "Remove booked event?",
        message: (
          <>
            “<span className="no-translate">{originalEvent.title}</span>” will
            be removed from your trip.
          </>
        ),
        cancelLabel: "No, keep it",
        okLabel: "Yes, remove",
        okAction: () => {
          void handleRemoveEvent(originalEvent)
        },
      })
    } else {
      return await handleRemoveEvent(originalEvent)
    }
  }

  async function removeScheduledEvents() {
    return await handleRemoveScheduledEvents()
  }

  async function removeIdeas() {
    return await handleRemoveIdeas()
  }

  async function undoEvent() {
    const undoResponse = await undoEventAction()
    return undoResponse?.response
  }

  async function redoEvent() {
    const redoResponse = await redoEventAction()
    return redoResponse?.response
  }

  function showToast(
    action: ItineraryActionToastAction,
    options: ItineraryActionToastOptions = {}
  ) {
    return showItineraryActionToast(action, {
      ...options,
      silent,
      tripSheetOpen,
      undoRedoLabel: getUndoRedoToastActionLabel(),
      onUndoRedoAction: handleUndoRedoToastAction,
    })
  }

  function showUndoableToast(
    action: Exclude<ItineraryActionToastAction, "bulk-undo" | "bulk-redo">,
    options: ItineraryActionToastOptions = {}
  ) {
    setUndoRedoToastState("undo")
    return showToast(action, options)
  }

  function getUndoRedoToastActionLabel() {
    return nextState === "redo" ? "Redo" : "Undo"
  }

  function handleUndoRedoToastAction() {
    if (nextState === "undo") {
      void undoEventAction()
    } else if (nextState === "redo") {
      void redoEventAction()
    }
  }

  async function handleAddEvent(
    addAction: PreparedAddItineraryEventAction
  ): Promise<ItineraryEvent | undefined> {
    const event = await createPreparedItineraryEvent({
      dispatch,
      tripRef: requireTripRef(),
      tripTimeFrame,
      addAction,
    })

    if (event) {
      const toastMessage = showUndoableToast("add", {
        context: getAddItineraryActionToastContext(event, addAction),
      })
      sendAnalyticsEvent({
        name: "ItineraryAdd",
        properties: {
          ...itineraryEventToAmplitudeProperties(event),
          toastMessage,
          uiWhere,
          target: event.scheduled ? "Itinerary" : "Ideas",
        },
      })
    }
    return event
  }

  async function handleRemoveEvent(event: ItineraryEvent) {
    const removeItineraryResponse = await removePlannedToSavedAction(event)
    if (removeItineraryResponse?.event) {
      sendAnalyticsEvent({
        name: "ItineraryRemove",
        properties: {
          ...itineraryEventToAmplitudeProperties(removeItineraryResponse.event),
          toastMessage: removeItineraryResponse.toastMessage,
          uiWhere,
        },
      })
    }
    return removeItineraryResponse
  }

  async function handleRemoveScheduledEvents() {
    return await handleClearItinerary(
      "events",
      "remove-scheduled-events",
      "ItineraryRemoveScheduledEvents"
    )
  }

  async function handleRemoveIdeas() {
    return await handleClearItinerary(
      "ideas",
      "remove-ideas",
      "ItineraryRemoveIdeas"
    )
  }

  async function handleClearItinerary(
    only: ItineraryClearOnly,
    toastAction: ItineraryActionToastClearAction,
    analyticsName: ItineraryToastClearAnalyticsName
  ): Promise<ClearItineraryActionResponse> {
    const cleared = await clearItinerarySection({
      dispatch,
      tripRef: requireTripRef(),
      only,
    })
    if (!cleared) return undefined

    const clearResponse = { toastMessage: showUndoableToast(toastAction) }
    sendAnalyticsEvent({
      name: analyticsName,
      properties: {
        toastMessage: clearResponse?.toastMessage,
        uiWhere,
      },
    })
    return clearResponse
  }

  async function handleChangeEvent(
    updateAction: PreparedUpdateItineraryEventAction
  ) {
    let moveConfirmed = false
    const event = await updatePreparedItineraryEvent({
      dispatch,
      tripRef: requireTripRef(),
      tripTimeFrame,
      updateAction,
      moveConfirmed,
      onMoveConfirmed: () => {
        moveConfirmed = true
      },
    })

    if (event) {
      const toastMessage = isItineraryMoveOnlyUpdate(updateAction)
        ? undefined
        : showUndoableToast("change", {
            context: getUpdateItineraryActionToastContext(updateAction),
          })
      sendAnalyticsEvent({
        name: "ItineraryUpdate",
        properties: {
          // Too much data output
          // from: {
          //   ...itineraryEventToAmplitudeProperties(updateAction.event),
          // },
          // to: {
          //   ...itineraryEventToAmplitudeProperties(event),
          // },
          ...itineraryEventToAmplitudeProperties(event),
          toastMessage,
          uiWhere,
          target: event.scheduled ? "Itinerary" : "Ideas",
        },
      })
    }
    return event
  }

  async function removePlannedToSavedAction(
    event: ItineraryEvent
  ): Promise<RemoveItineraryEventActionResponse | undefined> {
    const removed = await removeItineraryEventById({
      dispatch,
      eventId: event.id,
    })
    if (!removed) {
      return undefined
    }

    const toastMessage = showUndoableToast("remove", {
      context: getItineraryEventToastContext(event),
    })
    return { event: event, toastMessage: toastMessage }
  }

  async function undoEventAction(): Promise<UndoRedoResponseAndMessage> {
    return await handleUndoRedoEvent("undo")
  }

  async function redoEventAction(): Promise<UndoRedoResponseAndMessage> {
    return await handleUndoRedoEvent("redo")
  }

  async function handleUndoRedoEvent(
    direction: UndoRedoDirection
  ): Promise<UndoRedoResponseAndMessage> {
    const response = await runUndoRedoItinerary(direction)
    const ret = {
      response: response,
      toastMessage: showUndoRedoResponseToast(response, direction),
    }

    trackUndoRedoEvent(ret, direction)
    return ret
  }

  async function runUndoRedoItinerary(direction: UndoRedoDirection) {
    const runItinerary =
      direction === "undo" ? runUndoItinerary : runRedoItinerary
    return await runItinerary({
      dispatch,
      tripRef: requireTripRef(),
    })
  }

  function trackUndoRedoEvent(
    ret: UndoRedoResponseAndMessage,
    direction: UndoRedoDirection
  ) {
    if (direction === "undo") {
      sendAnalyticsEvent({
        name: "ItineraryUndo",
        properties: undoResponseToAmplitudeProperties(ret),
        uiWhere,
      })
      return
    }

    sendAnalyticsEvent({
      name: "ItineraryRedo",
      properties: redoResponseToAmplitudeProperties(ret),
      uiWhere,
    })
  }

  function showUndoRedoResponseToast(
    response: UndoRedoResponse,
    direction: UndoRedoDirection
  ) {
    const description = describeUndoRedoResponse(response, direction)
    if (description.warning) {
      if (!silent) {
        toastManager.add({ type: "info", description: description.warning })
      }
      return description.warning
    }
    if (!description.toast) {
      return undefined
    }

    setUndoRedoToastState(direction === "undo" ? "redo" : "undo")

    const { event } = description.toast
    return showToast(description.toast.action, {
      context: event ? getItineraryEventToastContext(event) : undefined,
      message: description.toast.message,
    })
  }

  function setUndoRedoToastState(
    toastNextState: ItineraryToastUndoRedoNextState
  ) {
    nextState = toastNextState
  }

  return {
    addPlaceEvent,
    addCustomEvent,
    changeEvent,
    removeEvent,
    removeScheduledEvents,
    removeIdeas,
    undoEvent,
    redoEvent,
  }
}

export type CreateItineraryActionsOptions = {
  dispatch: AppDispatch
  tripRef: TripRef | null
  uiWhere?: UIWhere
  tripTimeFrame?: TimeFrame
  silent?: boolean
  tripSheetOpen?: boolean
}

export type ItineraryActions = {
  addPlaceEvent: (
    addProps: AddPlaceItineraryEventActionInput
  ) => Promise<ItineraryEvent | undefined>
  addCustomEvent: (
    addCustomProps: AddCustomItineraryEventActionInput
  ) => Promise<ItineraryEvent | undefined>
  changeEvent: (
    changeProps: UpdateItineraryEventActionInput
  ) => Promise<ItineraryEvent | undefined>
  removeEvent: (
    originalEvent: ItineraryEvent
  ) => Promise<RemoveItineraryEventActionResponse | undefined>
  removeScheduledEvents: () => Promise<ClearItineraryActionResponse>
  removeIdeas: () => Promise<ClearItineraryActionResponse>
  undoEvent: () => Promise<UndoRedoResponse | undefined>
  redoEvent: () => Promise<UndoRedoResponse | undefined>
}

type ItineraryToastNextState = "init" | "undo" | "redo"
type ItineraryToastUndoRedoNextState = "undo" | "redo"
type ItineraryToastClearAnalyticsName =
  | "ItineraryRemoveScheduledEvents"
  | "ItineraryRemoveIdeas"
